import { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { useUnit } from 'effector-react';
import mapboxgl from 'mapbox-gl';
import log from 'eslint-plugin-react/lib/util/log.js';
import {
  $chPoints,
  $chQuery,
  changeMobilePopupEv,
} from '../../../models/centerHomeModel/index.js';
import { $mapLoaded } from '../../../models/mapModel/index.js';
import CenterHomePopup from '../../CenterHomePopup/CenterHomePopup.jsx';
import { flyTo } from '../../../utils/mapbox-utils.js';
import CenterHomeListPopup from '../../CenterHomePopup/CenterHomeListPopup.jsx';

export const handleClickPoint = (e) => {
  const feature = e.features[0];
  const coordinates = feature.geometry.coordinates.slice();
  if (window.location.search.includes('property_id')) {
    return '';
  }
  if (window.innerWidth > 500) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // const chQuery = useUnit($chQuery);
    // console.log('chQuery: - ', chQuery);
    // console.log('window.location.search: - ', window.location.search);

    window.chPopup = new mapboxgl.Popup()
      .setLngLat(coordinates)
      .setHTML('')
      .addTo(window.map);

    const popup = document.getElementsByClassName('mapboxgl-popup-content')[0];
    if (e.features.length > 1) {
      ReactDOM.createRoot(popup).render(
        <CenterHomeListPopup features={e.features} />
      );
    } else {
      flyTo([coordinates[0], coordinates[1] - 0.004], 15);
      ReactDOM.createRoot(popup).render(<CenterHomePopup feature={feature} />);
    }
  } else {
    changeMobilePopupEv(e.features);
    window.map.setFeatureState(
      { source: 'ch_points_source', id: feature.id },
      { active: true }
    );
    window.map.getSource('ch_active_points_source').setData({
      type: 'FeatureCollection',
      features: [feature],
    });
  }
};

const handleDoubleClickCluster = (e) => {
  const features = window.map.queryRenderedFeatures(e.point, {
    layers: [`ch_cluster_points_layer`],
  });
  const clusterId = features[0].properties.cluster_id;
  window.map
    .getSource(`ch_points_source`)
    .getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;

      window.map.easeTo({
        center: features[0].geometry.coordinates,
        zoom,
      });
    });
};

const handleClickCluster = (e) => {
  const cluster = e.features[0];
  const coordinates = cluster.geometry.coordinates.slice();

  window.map
    .getSource('ch_points_source')
    .getClusterLeaves(
      cluster.properties.cluster_id,
      cluster.properties.point_count,
      0,
      (err, features) => {
        if (!err) {
          if (window.innerWidth > 500) {
            if (window.chPopup) {
              window.chPopup.remove();
              delete window.chPopup;
            }
            window.chPopup = new mapboxgl.Popup()
              .setLngLat(coordinates)
              .setHTML('')
              .addTo(window.map);

            const popup = document.getElementsByClassName(
              'mapboxgl-popup-content'
            )[0];
            ReactDOM.createRoot(popup).render(
              <CenterHomeListPopup features={features} />
            );
          } else {
            changeMobilePopupEv(features);
          }
        }
      }
    );
};

function ChPoints() {
  const points = useUnit($chPoints);
  const mapLoaded = useUnit($mapLoaded);

  const createLayer = (update) => {
    const pointsData = {
      type: 'FeatureCollection',
      features: points,
    };

    if (update) {
      window.map.getSource('ch_points_source').setData(pointsData);
    } else {
      if (!window.map.getSource('ch_points_source')) {
        window.map.addSource('ch_points_source', {
          type: 'geojson',
          data: pointsData,
          cluster: true,
          // clusterMaxZoom: 14,
          clusterRadius: 50,
          generateId: true,
        });

        window.map.addSource('ch_active_points_source', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      if (!window.map.getLayer('ch_points_layer_labels')) {
        // FIXME ClusterLayer
        window.map.addLayer({
          id: 'ch_cluster_points_layer',
          type: 'circle',
          source: 'ch_points_source',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': 'white',
            'circle-stroke-color': '#b3b3b3',
            'circle-stroke-width': 6,
            'circle-radius': 15,
          },
        });

        window.map.addLayer({
          id: `ch_count_points_layer`,
          type: 'symbol',
          source: `ch_points_source`,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-size': 12,
          },
        });

        window.map.addLayer({
          id: `ch_points_layer_labels`,
          type: 'symbol',
          source: `ch_points_source`,
          filter: ['!', ['has', 'point_count']],
          layout: {
            'text-field': ['get', 'label'],
            'icon-text-fit': 'both',
            'icon-image': window.innerWidth < 500 ? 'mobile_popup' : 'popup',
            'icon-allow-overlap': true,
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': window.innerWidth < 500 ? '#000' : '#fff',
            'icon-opacity': [
              'case',
              ['boolean', ['feature-state', 'active'], false],
              0,
              1,
            ],
            'text-opacity': [
              'case',
              ['boolean', ['feature-state', 'active'], false],
              0,
              1,
            ],
          },
        });
        window.map.addLayer({
          id: `ch_active_points_layer_labels`,
          type: 'symbol',
          source: `ch_active_points_source`,
          filter: ['!', ['has', 'point_count']],
          layout: {
            'text-field': ['get', 'label'],
            'icon-text-fit': 'both',
            'icon-image': 'mobile_popup_active',
            'icon-allow-overlap': true,
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#fff',
          },
        });

        window.map.on('click', 'ch_points_layer_labels', handleClickPoint);

        window.map.on('mouseenter', `ch_cluster_points_layer`, () => {
          window.map.getCanvas().style.cursor = 'pointer';
        });

        window.map.on('mouseleave', `ch_cluster_points_layer`, () => {
          window.map.getCanvas().style.cursor = '';
        });

        window.map.on('mouseenter', `ch_points_layer_labels`, () => {
          window.map.getCanvas().style.cursor = 'pointer';
        });

        window.map.on('mouseleave', `ch_points_layer_labels`, () => {
          window.map.getCanvas().style.cursor = '';
        });

        window.map.on(
          'dblclick',
          'ch_cluster_points_layer',
          handleDoubleClickCluster
        );
        window.map.on('click', 'ch_cluster_points_layer', handleClickCluster);
      }
    }
  };

  useEffect(() => {
    if (mapLoaded && points) {
      if (window.map.getLayer('ch_points_layer_labels')) {
        createLayer(true);
      } else {
        setTimeout(() => {
          createLayer(false);
        }, 500);
      }
    }
  }, [mapLoaded, points]);

  return null;
}

export default ChPoints;
